import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useSearchParams, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { AuthLayout } from './components/layout/AuthLayout';
import { LoadingScreen } from './components/ui/LoadingScreen';
import { oaiqPageView } from './lib/oaiq';

import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { HomePage } from './pages/public/HomePage';
import { PricingPage } from './pages/public/PricingPage';
import { OfferPage } from './pages/public/OfferPage';
import { CheckoutPage } from './pages/public/CheckoutPage';
import { OnboardingPage } from './pages/onboarding/OnboardingPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { CampaignsPage } from './pages/dashboard/CampaignsPage';
import { LeadsPage } from './pages/dashboard/LeadsPage';
import { SitesPage } from './pages/dashboard/SitesPage';
import { SiteEditorPage } from './pages/dashboard/SiteEditorPage';
import CanvaSiteEditor from './pages/dashboard/CanvaSiteEditor';
import InlineSiteEditor from './pages/dashboard/InlineSiteEditor';
import { ThemeSelectPage } from './pages/dashboard/ThemeSelectPage';
import { EarningsPage } from './pages/dashboard/EarningsPage';
import { SettingsPage } from './pages/dashboard/SettingsPage';
import { SupportPage } from './pages/dashboard/SupportPage';
import { SupportInboxPage } from './pages/dashboard/SupportInboxPage';
import { PreviewSitePage } from './pages/public/PreviewSitePage';
import { PurchaseSuccessPage } from './pages/public/PurchaseSuccessPage';
import { PrivacyPolicyPage } from './pages/public/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/public/TermsOfServicePage';
import { DemoStateProvider } from './pages/demo/DemoStateProvider';
import { DemoLayout } from './pages/demo/DemoLayout';
import { DemoDashboard } from './pages/demo/DemoDashboard';
import { DemoCampaigns } from './pages/demo/DemoCampaigns';
import { DemoLeads } from './pages/demo/DemoLeads';
import { DemoSites } from './pages/demo/DemoSites';
import DemoCanvaSiteEditor from './pages/demo/DemoCanvaSiteEditor';
import { DemoComingSoon } from './pages/demo/DemoComingSoon';
import { DemoPreviewSitePage } from './pages/demo/DemoPreviewSitePage';

// Grandfathered legacy free-tier users keep dashboard access without an
// active subscription. Everyone else must have `subscriptionActive === true`.
const hasDashboardAccess = (userData: { subscriptionActive?: boolean; grandfathered?: boolean } | null) =>
  !!userData && (userData.subscriptionActive || userData.grandfathered);

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, userData } = useAuth();
  const [searchParams] = useSearchParams();
  const isCheckoutReturn = searchParams.get('checkout') === 'success';
  const [waitingForActivation, setWaitingForActivation] = useState(false);

  useEffect(() => {
    if (isCheckoutReturn && user && userData && !userData.subscriptionActive) {
      setWaitingForActivation(true);
      const timeout = setTimeout(() => setWaitingForActivation(false), 15000);
      return () => clearTimeout(timeout);
    }
  }, [isCheckoutReturn, user, userData?.subscriptionActive]);

  useEffect(() => {
    if (userData?.subscriptionActive && waitingForActivation) {
      setWaitingForActivation(false);
    }
  }, [userData?.subscriptionActive, waitingForActivation]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!userData) {
    return <LoadingScreen />;
  }

  // After checkout, wait briefly for the webhook to activate the subscription
  // before bouncing the user away - otherwise successful payments look broken.
  if (waitingForActivation) {
    return <LoadingScreen />;
  }

  if (!hasDashboardAccess(userData)) {
    return <Navigate to="/" replace />;
  }

  if (!userData.onboardingCompleted) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

function AuthRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, userData } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (user) {
    if (!userData) {
      return <LoadingScreen />;
    }
    if (!hasDashboardAccess(userData)) {
      return <Navigate to="/" replace />;
    }
    if (!userData.onboardingCompleted) {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function OnboardingRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, userData } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!userData) {
    return <LoadingScreen />;
  }

  if (!hasDashboardAccess(userData)) {
    return <Navigate to="/" replace />;
  }

  if (userData.onboardingCompleted) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// The OpenAI pixel sends no page_viewed of its own, and an SPA route change is
// not a page load - without this the ad account sees one page view per session.
function useOaiqPageViews() {
  const { pathname } = useLocation();
  useEffect(() => {
    oaiqPageView(pathname);
  }, [pathname]);
}

export default function App() {
  useOaiqPageViews();

  return (
    <Routes>
      {/* Public homepage - landing page */}
      <Route path="/" element={<HomePage />} />

      {/* Pricing page - entry point to hard checkout */}
      <Route path="/pricing" element={<PricingPage />} />
      {/* Exit offer. Stripe's cancel_url points here, so it is the first thing a
          buyer who backed out of Checkout sees. */}
      <Route path="/offer" element={<OfferPage />} />

      {/* Embedded checkout - native Payment Element with Romulus brand continuity */}
      <Route path="/checkout" element={<CheckoutPage />} />

      {/* Login for existing users */}
      <Route
        path="/login"
        element={
          <AuthRoute>
            <AuthLayout>
              <LoginPage />
            </AuthLayout>
          </AuthRoute>
        }
      />

      {/* Register - only accessible after Stripe payment (with session_id) */}
      <Route
        path="/register"
        element={
          <AuthLayout>
            <RegisterPage />
          </AuthLayout>
        }
      />

      {/* Onboarding after account creation */}
      <Route
        path="/onboarding"
        element={
          <OnboardingRoute>
            <OnboardingPage />
          </OnboardingRoute>
        }
      />

      {/* Protected dashboard routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="campaigns" element={<CampaignsPage />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="sites" element={<SitesPage />} />
        <Route path="earnings" element={<EarningsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="support" element={<SupportPage />} />
        <Route path="support-inbox" element={<SupportInboxPage />} />
      </Route>

      {/* Theme selection page for new websites */}
      <Route
        path="/dashboard/themes"
        element={
          <ProtectedRoute>
            <ThemeSelectPage />
          </ProtectedRoute>
        }
      />

      {/* New Canva-like fullscreen site editor */}
      <Route
        path="/dashboard/sites/:siteId/edit"
        element={
          <ProtectedRoute>
            <CanvaSiteEditor />
          </ProtectedRoute>
        }
      />

      {/* New inline (WYSIWYG) site editor - powered by GrapesJS */}
      <Route
        path="/dashboard/sites/:siteId/edit-inline"
        element={
          <ProtectedRoute>
            <InlineSiteEditor />
          </ProtectedRoute>
        }
      />

      {/* Legacy site editor (fallback) */}
      <Route
        path="/dashboard/sites/:siteId/edit-legacy"
        element={
          <ProtectedRoute>
            <SiteEditorPage />
          </ProtectedRoute>
        }
      />

      {/* Public live demo — fully fabricated, no auth, no backend writes.
          State lives in memory inside DemoStateProvider; refresh resets. */}
      <Route path="/demo" element={<DemoStateProvider><Outlet /></DemoStateProvider>}>
        <Route element={<DemoLayout />}>
          <Route index element={<DemoDashboard />} />
          <Route path="campaigns" element={<DemoCampaigns />} />
          <Route path="leads" element={<DemoLeads />} />
          <Route path="sites" element={<DemoSites />} />
          <Route path="earnings" element={<DemoComingSoon />} />
          <Route path="settings" element={<DemoComingSoon />} />
          <Route path="support" element={<DemoComingSoon />} />
        </Route>
        <Route path="sites/:siteId/edit" element={<DemoCanvaSiteEditor />} />
        <Route path="p/:token" element={<DemoPreviewSitePage />} />
      </Route>

      {/* Preview page (public, uses token for capability-based access) */}
      <Route path="/p/:token" element={<PreviewSitePage />} />

      {/* Purchase success page (public, completes purchase flow) */}
      <Route path="/purchase/success" element={<PurchaseSuccessPage />} />

      {/* Legal pages */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />

      {/* Default redirect to homepage */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
